import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';
import ApplicationLayout from 'v3/layouts/Application';
import { Container } from 'react-bootstrap';
import { DataTable, Text, Button } from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import useQuery from 'hooks/useQuery';
import UnattendedLoadsFilter from './Filter/index';
import AdvancedFilterModal from './Filter/AdvancedFilterModal/index';
import apiReport from 'services/apis/report';
import theme from 'v3/theme';
import FileSaver from 'file-saver';

const HeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`;

function formatCurrencyBRL(value) {
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch (e) {
    return value;
  }
}

function formatNumberBR(value, fractionDigits = 2) {
  try {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(Number(value || 0));
  } catch (e) {
    return value;
  }
}

function buildURLParams(advancedFilters) {
  const params = new URLSearchParams();

  if (advancedFilters?.initialProvince?.id) {
    params.append('initialProvince', advancedFilters.initialProvince.id);
  }
  if (advancedFilters?.lastProvince?.id) {
    params.append('lastProvince', advancedFilters.lastProvince.id);
  }
  if (advancedFilters?.loadingStart) {
    params.append('loadingStart', moment(advancedFilters.loadingStart).format('YYYY-MM-DD'));
  }
  if (advancedFilters?.loadingEnd) {
    params.append('loadingEnd', moment(advancedFilters.loadingEnd).format('YYYY-MM-DD'));
  }
  if (advancedFilters?.expired) {
    params.append('expired', '1');
  }
  if (advancedFilters?.status) {
    params.append('status', advancedFilters.status.slug);
  }

  if (advancedFilters?.costCenters?.length) {
    advancedFilters.costCenters.forEach(costCenter => {
      params.append('costCenters[]', costCenter.id);
    });
  }

  if (advancedFilters?.clients?.length) {
    advancedFilters.clients.forEach(client => {
      params.append('clients[]', client.id);
    });
  }

  if (advancedFilters?.users?.length) {
    advancedFilters.users.forEach(user => {
      params.append('users[]', user.id);
    });
  }

  if (advancedFilters?.businessGroups?.length) {
    advancedFilters.businessGroups.forEach(group => {
      params.append('businessGroupIds[]', group.id);
    });
  }

  return params;
}

export default function UnattendedLoads() {
  const snackbar = useSnackbar();
  const queryParams = useQuery();

  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [data, setData] = useState([]);
  const [qtdAdvancedFilters, setQtdAdvancedFilters] = useState(0);
  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState(false);

  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    currentPage: 1,
  });

  const [advancedFilters, setAdvancedFilters] = useState({
    initialProvince: null,
    lastProvince: null,
    loadingStart: null,
    loadingEnd: null,
    expired: false,
    status: null,
    costCenters: [],
    clients: [],
    users: [],
    businessGroupIds: [],
    page: 1,
    per_page: 10,
  });

  function handleTitleAdvancedFilters() {
    let title = 'Filtro avançado';
    if (qtdAdvancedFilters) {
      title += ` (${qtdAdvancedFilters})`;
    }
    return title;
  }

  const exportReport = async () => {
    try {
      setLoadingExport(true);
      const params = buildURLParams(advancedFilters);
      const response = await apiReport.get(`/export-unattended-loads?${params.toString()}`, { responseType: 'blob' });
      FileSaver.saveAs(response.data, response.headers['x-file-name']);
    } catch (error) {
      setLoadingExport(false);
      const message = 'Ocorreu um erro ao exportar o relatório.';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoadingExport(false);
    }
  }

  const fetchData = useCallback(async (advancedFilters) => {
    setLoading(true);
    try {
      const params = buildURLParams(advancedFilters);
      params.append('page', advancedFilters?.page);
      params.append('per_page', advancedFilters?.per_page);

      const response = await apiReport.get(`/unattended-loads?${params.toString()}`);

      const { items = [], pagination = {} } = response.data;

      setPagination({
        total: pagination.total || 0,
        lastPage: pagination.lastPage || 1,
        currentPage: advancedFilters.page,
      });
      setData(items);
    } catch (error) {
      setData([]);
      const message = error?.response?.data?.message || 'Ocorreu um erro ao carregar o relatório.';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [advancedFilters, snackbar]);

  async function changePage(page) {
    setAdvancedFilters({ ...advancedFilters, page });
    fetchData({ ...advancedFilters, page });
  }

  async function changePerPage(per_page) {
    setAdvancedFilters({ ...advancedFilters, per_page });
    fetchData({ ...advancedFilters, per_page });
  }

  useEffect(() => {
    const newFilters = {
      initialProvince: queryParams?.get('initialProvince') ? { id: Number(queryParams.get('initialProvince')) } : null,
      lastProvince: queryParams?.get('lastProvince') ? { id: Number(queryParams.get('lastProvince')) } : null,
      loadingStart: queryParams?.get('loadingStart') || null,
      loadingEnd: queryParams?.get('loadingEnd') || null,
      expired: queryParams?.get('expired') === '1' || queryParams?.get('expired') === 'true',
      page: queryParams?.get('page') ? Number(queryParams.get('page')) : 1,
      per_page: queryParams?.get('per_page') ? Number(queryParams.get('per_page')) : 10,
      status: queryParams?.get('status') ? { label: queryParams.get('status') === 'ATTENDANCE' ? 'Atendida' : 'Não atendida', slug: queryParams.get('status') } : null,
      costCenters: queryParams?.getAll('costCenters[]')?.length ? queryParams.getAll('costCenters[]').map(id => ({ id: Number(id) })) : [],
      clients: queryParams?.getAll('clients[]')?.length ? queryParams.getAll('clients[]').map(id => ({ id: Number(id) })) : [],
      users: queryParams?.getAll('users[]')?.length ? queryParams.getAll('users[]').map(id => ({ id: Number(id) })) : [],
      businessGroups: queryParams?.getAll('businessGroupId[]')?.length ? queryParams.getAll('businessGroupId[]').map(id => ({ id: Number(id) })) : [],
    };
    setAdvancedFilters(newFilters);
    fetchData(newFilters);
  }, []);

  function onChangeFilters(filters) {
    const newFilters = {
      ...filters,
      page: 1,
    }
    setAdvancedFilters(newFilters);
    fetchData(newFilters);
  }

  return (
    <ApplicationLayout
      title={"Grid de cargas"}
      contentFluid={true}
      RightComponent={
        <HeaderActions>
          <Button variant="success" loading={loadingExport} onClick={exportReport}>
            <Text color="white" weight={500}>Exportar</Text>
          </Button>
          <Button variant="secondary" onClick={() => setShowAdvancedFiltersModal(true)}>
            <Text weight={500} color="dark" type="regular">
              {handleTitleAdvancedFilters()}
            </Text>
          </Button>
        </HeaderActions>
      }
    >
      <>
        <Container style={{ marginBottom: '3rem' }}>
          <UnattendedLoadsFilter
            loading={loading}
            onChange={onChangeFilters}
            setAdvancedFilters={setAdvancedFilters}
            advancedFilters={advancedFilters}
          />
        </Container>
        <DataTable
          data={data}
          loading={loading}
          withFilterOption={false}
          page={pagination?.currentPage}
          pages={pagination?.lastPage}
          total={pagination?.total}
          onPageChanged={changePage}
          onPerPageChanged={changePerPage}
          defaultPerPage={advancedFilters?.per_page}
          perPageOptions={[{ v: 10 }, { v: 20 }, { v: 30 }, { v: 50 }]}
          tableStyles={{
            whiteSpace: 'nowrap',
            borderCollapse: 'collapse',
            '> thead > tr > th': {
              borderBottom: '2px solid #c5ccd5',
            },
            '> tbody > tr': {
              borderBottom: '1px solid #d8dee6',
            },
            '> tbody > tr:hover': {
              background: '#f3f6fa',
            },
            '> tbody > tr:hover > td': {
              background: '#f3f6fa',
            },
          }}

          columns={[
            {
              name: 'Status', selector: row => row?.status === 'ATTENDANCE' ? (
                <Text color={theme.colors.success} weight={600}>Atendida</Text>
              ) : (
                <Text color={theme.colors.danger} weight={600}>Não atendida</Text>
              ) || '-'
            },
            {
              name: 'Id da carga', selector: row => (
                <Link to={`/cargas/${row?.id}`} target="_blank">
                  <Text size="small" fontSize="0.875rem" className="underline" style={{ textDecoration: 'underline' }} color={theme.colors.light_blue}>{row?.id}</Text>
                </Link>
              )
            },
            {
              name: 'Id da viagem', selector: row => (
                row?.viagem_id ? (
                  <Link to={`/viagens/${row?.viagem_id}`} target="_blank">
                    <Text size="small" fontSize="0.875rem" className="underline" style={{ textDecoration: 'underline' }} color={theme.colors.light_blue}>{row?.viagem_id}</Text>
                  </Link>
                ) : '-'
              )
            },
            {
              name: 'Cliente', selector: row => (
                <Link to={`/clientes/${row?.cliente_id}`} style={{ color: 'inherit', textDecoration: 'none' }} target="_blank">
                  <div>
                    <Text weight={600} fontSize="0.875rem">{row?.cliente || '-'}</Text><br />
                    <Text size="small" fontSize="0.875rem" className="underline" style={{ textDecoration: 'underline' }} color={theme.colors.light_blue}>{row?.cliente_cgccpf || '-'}</Text>
                  </div>
                </Link>
              )
            },
            { name: 'Centro de custo', selector: row => row?.centro_custo || '-' },
            { name: 'Filial', selector: row => row?.filial || '-' },
            { name: 'Número pedido', selector: row => row?.pedido_cliente || '-' },
            { name: 'Produto', selector: row => row?.produto || '-' },
            {
              name: 'Transportador', selector: row => (
                row?.transportadora_id ? (
                  <Link to={`/transportadoras/${row?.transportadora_id}`} style={{ color: 'inherit', textDecoration: 'none' }} target="_blank">
                    <div>
                      <Text weight={600} fontSize="0.875rem">{row?.transportadora_nome || '-'}</Text><br />
                      <Text size="small" fontSize="0.875rem" className="underline" style={{ textDecoration: 'underline' }} color={theme.colors.light_blue}>{row?.transportadora_cgccpf}</Text>
                    </div>
                  </Link>
                ) : '-'
              )
            },
            { name: 'Origem', selector: row => `${row?.cidade_origem} / ${row?.uf_origem}` },
            { name: 'Destino', selector: row => `${row?.cidade_destino} / ${row?.uf_destino}` },
            { name: 'Carroceria', selector: row => row?.tipo_carroceria || '-' },
            {
              name: 'Data da coleta', selector: row => row?.data_coleta ? (
                <Text color={theme.colors.dark_blue} weight={600} fontSize="0.875rem">{moment(row.data_coleta).format('DD/MM/YYYY HH:mm:ss')}</Text>
              ) : '-'
            },
            {
              name: 'Data da coleta máxima', selector: row => row?.data_coleta_maxima ? (
                <Text color={theme.colors.dark_blue} weight={600} fontSize="0.875rem">{moment(row.data_coleta_maxima).format('DD/MM/YYYY HH:mm:ss')}</Text>
              ) : '-'
            },
            { name: 'Data criação carga', selector: row => row?.data_criacao ? moment(row.data_criacao).format('DD/MM/YYYY') : '-' },
            {
              name: 'Data expiração', selector: row => row?.data_expiracao ? (
                <Text color={theme.colors.danger} weight={600} fontSize="0.875rem">{moment(row.data_expiracao).format('DD/MM/YYYY HH:mm:ss')}</Text>
              ) : '-'
            },
            {
              styles: {
                wordBreak: 'break-all',
                maxWidth: '400px',
                whiteSpace: 'break-spaces',
              },
              name: 'Observações da carga', selector: row =>
                row?.observacoes || '-'
            },
            {
              name: 'Contratos margem', selector: row => formatNumberBR(row?.contratos_margem, 2)
            },
            {
              name: 'Contratos rentabilidade', selector: row => formatNumberBR(row?.contratos_rentabilidade, 2)
            },
            {
              name: 'Frete negociado', selector: row => row?.frete_negociado ? formatCurrencyBRL(row?.frete_negociado) : '-'
            },
            {
              name: 'Valor tomador', selector: row => formatCurrencyBRL(row?.valor_tomador)
            },
            { name: 'KM', selector: row => formatNumberBR(row?.km_total, 0) },
            { name: 'Real KM', selector: row => formatNumberBR(row?.real_km, 2) },
          ]}
        />
      </>
      <AdvancedFilterModal
        onChange={onChangeFilters}
        loading={loading || loadingExport}
        showAdvancedFiltersModal={showAdvancedFiltersModal}
        setShowAdvancedFiltersModal={setShowAdvancedFiltersModal}
        setQtdAdvancedFilters={setQtdAdvancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        advancedFilters={advancedFilters}
      />

    </ApplicationLayout>
  );
}
